import React, { useEffect, useRef, useState } from "react";
import { CaretUp, CaretDown, Lock, CheckCircle, UsersThree } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { useI18n } from "../contexts/I18nContext";
import TeamFlag from "./TeamFlag";
import { parseDate } from "../lib/utils";
import { useActiveGroup } from "../contexts/ActiveGroupContext";
import { useToast } from "../hooks/use-toast";

function formatKickoff(iso, locale) {
  const d = parseDate(iso);
  if (!d) return "";
  const opts = { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" };
  return d.toLocaleDateString(locale === "ru" ? "ru-RU" : "en-US", opts);
}

function timeUntil(iso, locale) {
  const d = parseDate(iso);
  if (!d) return null;
  const diff = d.getTime() - Date.now();
  if (diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const isRu = locale === "ru";
  const dSuffix = isRu ? "д" : "d";
  const hSuffix = isRu ? "ч" : "h";
  const mSuffix = isRu ? "м" : "m";
  if (days > 0) return `${days}${dSuffix} ${hours}${hSuffix}`;
  const m = Math.floor((diff % 3600000) / 60000);
  return `${hours}${hSuffix} ${m}${mSuffix}`;
}

function Stepper({ value, onChange, disabled, testidPrefix }) {
  const inc = () => onChange(Math.min(20, (value ?? 0) + 1));
  const dec = () => onChange(Math.max(0, (value ?? 0) - 1));
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        data-testid={`${testidPrefix}-inc`}
        onClick={inc}
        disabled={disabled}
        className="w-8 h-6 flex items-center justify-center text-[#41a42b] disabled:text-neutral-300 active:scale-90 transition-transform"
      >
        <CaretUp size={20} weight="bold" />
      </button>
      <motion.div
        key={value}
        initial={{ scale: 0.85, opacity: 0.6 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.15 }}
        className={`w-14 h-14 rounded-xl border-2 ${disabled ? "border-neutral-200 bg-neutral-50" : "border-[#02371b] bg-white"} flex items-center justify-center`}
      >
        <span className={`font-mono-tab text-2xl font-bold ${disabled ? "text-neutral-400" : "text-[#02371b]"}`} data-testid={`${testidPrefix}-value`}>
          {value ?? "–"}
        </span>
      </motion.div>
      <button
        type="button"
        data-testid={`${testidPrefix}-dec`}
        onClick={dec}
        disabled={disabled}
        className="w-8 h-6 flex items-center justify-center text-[#41a42b] disabled:text-neutral-300 active:scale-90 transition-transform"
      >
        <CaretDown size={20} weight="bold" />
      </button>
    </div>
  );
}

export default function MatchCard({ match, onSave, onOpenBets }) {
  const { t, locale } = useI18n();
  const { toast } = useToast();
  const [home, setHome] = useState(match.user_bet?.home_score ?? null);
  const [away, setAway] = useState(match.user_bet?.away_score ?? null);
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const [isEditing, setIsEditing] = useState(false);
  const locked = match.locked;
  const isLive = match.locked && !match.finished;
  const startsIn = timeUntil(match.kickoff_at, locale);
  const hasExisting = !!match.user_bet;
  const lastMatchId = useRef(match.match_id);
  const saveTimer = useRef(null);
  const lastSavedValue = useRef({
    h: match.user_bet?.home_score ?? null,
    a: match.user_bet?.away_score ?? null
  });
  const staleGuard = useRef(null);
  const { activeGroup } = useActiveGroup();

  const pts = activeGroup?.scoring_system === "four_point" ? match.user_bet?.points_four_point : match.user_bet?.points;

  // Track the most up-to-date user selection in a ref
  const currentInput = useRef({ h: home, a: away });
  useEffect(() => {
    currentInput.current = { h: home, a: away };
  }, [home, away]);

  // Ref to always call the latest save callback on unmount without re-running effects
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    if (lastMatchId.current !== match.match_id) {
      lastMatchId.current = match.match_id;
      const initialHome = match.user_bet?.home_score ?? null;
      const initialAway = match.user_bet?.away_score ?? null;
      lastSavedValue.current = { h: initialHome, a: initialAway };
      staleGuard.current = null;
      setIsEditing(false);
      setStatus("idle");
      setHome(initialHome);
      setAway(initialAway);
      return;
    }

    if (isEditing) return;

    const propHome = match.user_bet?.home_score ?? null;
    const propAway = match.user_bet?.away_score ?? null;

    if (staleGuard.current !== null) {
      // We are waiting for the server to confirm our save.
      // If the incoming prop doesn't match our saved value yet, ignore it (it's stale).
      if ((propHome ?? null) !== staleGuard.current.h || (propAway ?? null) !== staleGuard.current.a) {
        return;
      }
      // Server has confirmed our save! Stop guarding.
      staleGuard.current = null;
    }

    lastSavedValue.current = { h: propHome, a: propAway };
    setHome(propHome);
    setAway(propAway);
  }, [match.match_id, match.user_bet?.home_score, match.user_bet?.away_score, isEditing]);

  // Flush unsaved changes on unmount
  useEffect(() => {
    return () => {
      const latest = currentInput.current;
      const lastSaved = lastSavedValue.current;
      const changed = latest.h !== lastSaved.h || latest.a !== lastSaved.a;
      const isDefaultUninteracted = !hasExisting && latest.h === 0 && latest.a === 0 && lastSaved.h === null;
      
      if (changed && !isDefaultUninteracted) {
        onSaveRef.current(match.match_id, latest.h, latest.a).catch((err) => {
          console.error("Failed to save on unmount", err);
        });
      }
    };
  }, [match.match_id, hasExisting]);

  // Autosave with debounce
  useEffect(() => {
    if (locked) return;
    const changed = home !== lastSavedValue.current.h || away !== lastSavedValue.current.a;
    if (!changed) return;
    if (!hasExisting && home === 0 && away === 0 && lastSavedValue.current.h === null) {
      // don't autosave default 0:0 if user hasn't interacted
      return;
    }
    
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
    }
    
    // Capture the target values for this debounce cycle
    const hToSave = home;
    const aToSave = away;

    saveTimer.current = setTimeout(async () => {
      setStatus("saving");
      try {
        await onSave(match.match_id, hToSave, aToSave);
        // Only finalize save state if user has not changed selection since timer started
        if (currentInput.current.h === hToSave && currentInput.current.a === aToSave) {
          lastSavedValue.current = { h: hToSave, a: aToSave };
          staleGuard.current = { h: hToSave, a: aToSave };
          setStatus("saved");
          setTimeout(() => {
            if (currentInput.current.h === hToSave && currentInput.current.a === aToSave) {
              setStatus("idle");
            }
          }, 1400);
          setIsEditing(false);
        }
      } catch (e) {
        if (currentInput.current.h === hToSave && currentInput.current.a === aToSave) {
          setStatus("error");
          toast({
            title: t("error_saving_prediction"),
            description: `${match.home_team?.name} vs ${match.away_team?.name}`,
            variant: "destructive",
          });
        }
      }
    }, 800);
    
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [home, away, locked]);

  // Touch step -> mark dirty so 0:0 after interaction saves
  const touch = (setter, nextValue, isHome) => {
    setIsEditing(true);
    setStatus("idle"); // Clear any previous errors on user interaction
    if (home === null || away === null) {
      const nextHome = isHome ? nextValue : 0;
      const nextAway = isHome ? 0 : nextValue;
      setHome(nextHome);
      setAway(nextAway);
      lastSavedValue.current = { h: -1, a: -1 };
    } else {
      setter(nextValue);
    }
  };

  const handleCardClick = () => {
    if (locked && onOpenBets) onOpenBets(match.match_id);
  };

  return (
    <motion.article
      initial={{ y: 8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.2 }}
      className={`bg-white rounded-2xl border p-4 mb-3 relative transition-all ${
        isLive
          ? "border-red-200 shadow-[0_0_12px_rgba(239,68,68,0.05)]"
          : "border-neutral-200"
      } ${locked ? "cursor-pointer" : ""} ${
        isLive
          ? "hover:border-red-300"
          : locked
          ? "hover:border-neutral-300"
          : ""
      }`}
      data-testid={`match-card-${match.match_id}`}
      onClick={locked ? handleCardClick : undefined}
    >
      <div className="flex items-center justify-between text-[10px] font-bold tracking-wider uppercase text-neutral-400 mb-3">
        <span>{formatKickoff(match.kickoff_at, locale)}</span>
        {isLive ? (
          <span className="flex items-center gap-1 text-red-600 font-bold animate-pulse">
            <span className="w-1.5 h-1.5 rounded-full bg-red-600" />
            LIVE
          </span>
        ) : locked ? (
          <span className="flex items-center gap-1 text-neutral-500">
            <Lock size={10} weight="bold" /> {t("locked")}
          </span>
        ) : startsIn ? (
          <span className="text-[#41a42b]">{t("starts_in")} · {startsIn}</span>
        ) : null}
      </div>

      <div className="text-[10px] text-center text-neutral-400 mb-3 font-medium">{match.stadium}</div>

      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <div className="flex flex-col items-center gap-2">
          <TeamFlag team={match.home_team} size={52} />
          <span className="font-bold text-xs text-center text-[#02371b] line-clamp-2 leading-tight">{match.home_team?.name}</span>
        </div>

        {locked ? (
          <div className="flex items-center gap-3 pt-1" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="font-mono-tab text-[10px] text-neutral-400 uppercase tracking-wider">
                {isLive ? (
                  <span className="text-red-600 font-bold flex items-center justify-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
                    LIVE
                  </span>
                ) : (
                  t("actual")
                )}
              </div>
              <div className={`font-mono-tab text-3xl font-bold leading-none mt-1 transition-colors duration-300 ${isLive ? "text-red-600" : "text-[#02371b]"}`}>
                {match.home_score ?? "–"}:{match.away_score ?? "–"}
              </div>
              {match.finished && (
                <>
                  {match.match_duration === "PENALTY_SHOOTOUT" && (
                    <div className="text-[9px] font-bold text-neutral-500 mt-1 uppercase tracking-wider">
                      {t("penalties")} {match.home_score_penalties} - {match.away_score_penalties}
                    </div>
                  )}
                  {match.match_duration === "EXTRA_TIME" && (
                    <div className="text-[9px] font-bold text-neutral-500 mt-1 uppercase tracking-wider">
                      {t("after_extra_time")}
                    </div>
                  )}
                  {activeGroup?.scoring_system === "four_point" &&
                   (match.match_duration === "EXTRA_TIME" || match.match_duration === "PENALTY_SHOOTOUT") &&
                   (match.home_score !== match.home_score_regular || match.away_score !== match.away_score_regular) && (
                    <div className="text-[9px] text-neutral-400 mt-0.5">
                      {t("main_time")}: {match.home_score_regular ?? 0}:{match.away_score_regular ?? 0}
                    </div>
                  )}
                </>
              )}
              {hasExisting && (
                <div className="font-mono-tab text-[10px] mt-2 text-neutral-500">
                  {t("you")}: {match.user_bet.home_score}:{match.user_bet.away_score}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
            <Stepper value={home} onChange={(v) => touch(setHome, v, true)} disabled={locked} testidPrefix={`${match.match_id}-home`} />
            <span className="font-display text-2xl text-neutral-300 select-none pb-1">:</span>
            <Stepper value={away} onChange={(v) => touch(setAway, v, false)} disabled={locked} testidPrefix={`${match.match_id}-away`} />
          </div>
        )}

        <div className="flex flex-col items-center gap-2">
          <TeamFlag team={match.away_team} size={52} />
          <span className="font-bold text-xs text-center text-[#02371b] line-clamp-2 leading-tight">{match.away_team?.name}</span>
        </div>
      </div>

      {match.match_report?.goals?.length > 0 && (
        <div className="mt-3 pt-2 text-[10px] text-neutral-500 font-medium">
          <div className="flex justify-between px-2 gap-4">
            <div className="flex flex-col items-start gap-0.5 text-left flex-1 overflow-hidden">
              {match.match_report.goals.filter(g => g.side === 'TEAM_A').sort((a, b) => (a.minute + (a.extra || 0)) - (b.minute + (b.extra || 0))).map((g, i) => (
                <div key={i} className="w-full flex items-center gap-1">
                  <span className="truncate">{g.scorer}</span>
                  <span className="opacity-60 shrink-0">{g.minute}{g.extra ? `+${g.extra}` : ''}'</span>
                  {g.type === 'PENALTY_GOAL' && <span className="opacity-60 shrink-0">(P)</span>}
                  {g.type === 'OWN_GOAL' && <span className="opacity-60 text-red-400 shrink-0">(OG)</span>}
                </div>
              ))}
            </div>
            <div className="flex flex-col items-end gap-0.5 text-right flex-1 overflow-hidden">
              {match.match_report.goals.filter(g => g.side === 'TEAM_B').sort((a, b) => (a.minute + (a.extra || 0)) - (b.minute + (b.extra || 0))).map((g, i) => (
                <div key={i} className="w-full flex items-center justify-end gap-1">
                  {g.type === 'OWN_GOAL' && <span className="opacity-60 text-red-400 shrink-0">(OG)</span>}
                  {g.type === 'PENALTY_GOAL' && <span className="opacity-60 shrink-0">(P)</span>}
                  <span className="opacity-60 shrink-0">{g.minute}{g.extra ? `+${g.extra}` : ''}'</span>
                  <span className="truncate">{g.scorer}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center justify-between">
        <span className="text-[11px] text-neutral-500 font-medium">
          <span className="flex items-center gap-1.5 text-neutral-500">
            <UsersThree size={12} weight="bold" /> {match.total_bets || 0} {t("tips_count")}
          </span>
        </span>
        {locked ? (
          pts != null ? (
            <span className="font-mono-tab text-sm font-bold text-[#41a42b]">+{pts} {t("pts")}</span>
          ) : (
            <span className="text-[10px] font-bold tracking-wider uppercase text-neutral-400">{t("tap_to_see_tips")} →</span>
          )
        ) : (
          <span
            className={`text-[11px] font-bold tracking-wider uppercase transition-colors ${
              status === "saving"
                ? "text-neutral-400"
                : status === "saved"
                ? "text-[#084d25]"
                : status === "error"
                ? "text-red-500 font-semibold animate-shake"
                : !hasExisting
                ? "text-amber-600"
                : "text-neutral-300"
            }`}
            data-testid={`status-${match.match_id}`}
          >
            {status === "saving" && <span>{t("saving")}</span>}
            {status === "saved" && <span>{t("saved")}</span>}
            {status === "error" && <span>{t("error_saving_prediction")}</span>}
            {status === "idle" && (
              hasExisting ? <span>{t("saved")}</span> : <span className="text-amber-600 font-semibold">{t("not_tipped")}</span>
            )}
          </span>
        )}
      </div>
    </motion.article>
  );
}
